/**
 * Regulatory — surety bond request letters.
 *
 * A request is the letter LCTF (the principal) sends to its bond agent asking
 * the surety to write a bond on one project. Saved in `suretyBondRequests`;
 * agent / signer defaults live in `financeSettings/suretyBonding`.
 */

export type SuretyBondType = 'bid' | 'performance_payment';

export type SuretyBondRequestStatus = 'draft' | 'sent' | 'issued' | 'declined';

export type SuretyObligeeRole = 'owner' | 'general_contractor';

export interface SuretyAgent {
  agency: string;
  contact: string;
  email: string;
  phone: string;
  /** Multi-line mailing address. */
  address: string;
}

export interface SuretySigner {
  name: string;
  title: string;
}

export interface SuretyObligee {
  role: SuretyObligeeRole;
  name: string;
  /** Multi-line mailing address. */
  address: string;
}

export type SuretySchedulePhaseKey = 'design' | 'procurement' | 'production' | 'installation';

/** One bar on the Project schedule enclosure. */
export interface SuretySchedulePhase {
  key: SuretySchedulePhaseKey | string;
  name: string;
  /** YYYY-MM-DD, local. */
  startDate: string;
  endDate: string;
  /** Where the dates came from — Hub tasks, the simulated split, or typed by hand. */
  source: 'tasks' | 'simulated' | 'manual';
}

export interface SuretyScheduleEnclosure {
  phases: SuretySchedulePhase[];
  note: string;
  /** YYYY-MM-DD the enclosure was generated. */
  builtAt: string;
}

export interface SuretyContractSummaryChangeOrder {
  number: number;
  title: string;
  amount: number;
  approvedAt: string | null;
}

export interface SuretyContractSummaryInstallment {
  id: string;
  label: string;
  /** 0–100. */
  percent: number;
  amount: number;
  dueDate: string | null;
  estimated: boolean;
  /** Plain-English anchor, e.g. "on contract signing". */
  basis: string;
}

export interface SuretyContractSummaryEnclosure {
  signedBase: number | null;
  currentContractValue: number;
  changeOrders: SuretyContractSummaryChangeOrder[];
  installments: SuretyContractSummaryInstallment[];
  builtAt: string;
}

export interface SuretyEnclosureDocs {
  schedule?: SuretyScheduleEnclosure | null;
  contractSummary?: SuretyContractSummaryEnclosure | null;
}

export interface SuretyContractRange {
  min: number;
  max: number;
  /** Plain-English reason, e.g. "structure arrangement and species". */
  basis: string;
}

export interface SuretyBondRequest {
  id: string;
  dealId: string;
  projectId: string;
  projectLabel: string;
  projectNumber: string;
  bondType: SuretyBondType;
  status: SuretyBondRequestStatus;
  /** YYYY-MM-DD, local. */
  letterDate: string;
  /**
   * Contract (or bid) amount the bond is sized from. When `contractRange` is
   * set this is the top of the range, so the penal sum covers every selection.
   */
  contractAmount: number;
  /**
   * Unsigned proposals with client selections (structure arrangement, options,
   * species, installation) — the letter quotes this range instead of one number.
   */
  contractRange?: SuretyContractRange | null;
  /** Percent of the contract amount the bond must cover (bid bonds are usually 5–10, P&P 100). */
  bondPercent: number;
  bondAmount: number;
  /** YYYY-MM-DD or null. */
  bidDate: string | null;
  contractDate: string | null;
  startDate: string | null;
  completionDate: string | null;
  warrantyMonths: number;
  obligee: SuretyObligee;
  siteAddress: string;
  scopeOfWork: string;
  /** Bond form the obligee requires — AIA A312, statutory, owner's form. */
  bondForm: string;
  neededBy: string | null;
  retainagePercent: number | null;
  liquidatedDamagesPerDay: number | null;
  architectEngineer: string;
  subcontractedPercent: number | null;
  designInScope: boolean;
  /** Printed enclosures line; generated ones carry the "Enclosure A — " prefix. */
  enclosures: string[];
  /** Pages the wizard generated behind the letter. */
  enclosureDocs?: SuretyEnclosureDocs | null;
  notes: string;
  /** Snapshot of the agent at save time so old letters do not change. */
  agent: SuretyAgent;
  signer: SuretySigner;
  bondNumber: string;
  suretyName: string;
  sentAt: Date | null;
  issuedAt: Date | null;
  createdAt: Date | null;
  createdBy: string;
  updatedAt: Date | null;
  updatedBy: string;
}

/** `financeSettings/suretyBonding`. */
export interface SuretyBondingSettings {
  agent: SuretyAgent;
  signer: SuretySigner;
  defaultBidBondPercent: number;
  defaultWarrantyMonths: number;
  defaultBondForm: string;
}

export const SURETY_BOND_TYPE_LABELS: Record<SuretyBondType, string> = {
  bid: 'Bid bond',
  performance_payment: 'Performance & Payment bonds',
};

export const SURETY_BOND_REQUEST_STATUS_LABELS: Record<SuretyBondRequestStatus, string> = {
  draft: 'Draft',
  sent: 'Sent to agent',
  issued: 'Bond issued',
  declined: 'Declined',
};

export const SURETY_OBLIGEE_ROLE_LABELS: Record<SuretyObligeeRole, string> = {
  owner: 'Owner',
  general_contractor: 'General contractor',
};

/** Only the schedule by default — the wizard generates it; WIP / backlog is attached by hand when a surety asks. */
export const DEFAULT_SURETY_ENCLOSURES: string[] = ['Project schedule'];
